import { Combobox } from '@/global/components/Combobox'
import { useDebouncedValue } from '@/hooks'
import { useCallback, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { type Cliente, clientes } from 'service/api'

const DEBOUNCE_BUSCA_MS = 300

type SelectClientesProps = {
    name: string
    label?: string
    /** Texto do gatilho quando nada está selecionado. */
    placeholder?: string
    disabled?: boolean
}

/**
 * Campo de clientes ligado ao react-hook-form — grava os ids escolhidos.
 *
 * Busca e paginação ficam no servidor porque a API limita `perPage` a 100:
 * pedir a lista inteira de uma vez esconderia os clientes seguintes.
 */
export function SelectClientes({
    name,
    label = 'Clientes',
    placeholder,
    disabled,
}: SelectClientesProps) {
    const { control } = useFormContext()
    const [busca, setBusca] = useState('')
    const buscaDebounced = useDebouncedValue(busca.trim(), DEBOUNCE_BUSCA_MS)

    const {
        data: paginas,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
    } = clientes.useInfiniteScroll<Cliente>({
        queryParams: (buscaDebounced
            ? { search: buscaDebounced }
            : {}) as never,
    })

    const carregados = useMemo(
        () => paginas?.pages.flatMap(pagina => pagina.data) ?? [],
        [paginas],
    )

    const opcoes = useMemo(
        () => carregados.map(cliente => cliente.id),
        [carregados],
    )

    const nomePorId = useMemo(
        () => new Map(carregados.map(cliente => [cliente.id, cliente])),
        [carregados],
    )

    // O cliente selecionado pode estar fora das páginas já carregadas — nesse
    // caso só o id é conhecido.
    const rotuloDe = useCallback(
        (id: number) => nomePorId.get(id)?.nomeFantasia ?? `#${id}`,
        [nomePorId],
    )

    const carregarMais = useCallback(() => {
        if (!isFetchingNextPage) fetchNextPage()
    }, [fetchNextPage, isFetchingNextPage])

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={[]}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Combobox<number>
                    id={`${name}-select`}
                    multiple
                    label={label}
                    placeholder={placeholder}
                    options={opcoes}
                    value={value ?? []}
                    onChange={onChange}
                    getOptionLabel={rotuloDe}
                    getOptionKey={id => String(id)}
                    isOptionEqualToValue={(opcao, id) => opcao === id}
                    inputValue={busca}
                    onInputChange={setBusca}
                    loading={isFetching && !isFetchingNextPage}
                    hasMore={hasNextPage}
                    onLoadMore={carregarMais}
                    loadingMore={isFetchingNextPage}
                    disabled={disabled || (control as any)._options?.disabled}
                    noOptionsText="Nenhum cliente encontrado"
                    unHelperText={false}
                    error={!!error}
                    helperText={error?.message}
                />
            )}
        />
    )
}
